<template>
  <div class="my-vouchers">
    <el-header>
      <div class="header-content">
        <el-button icon="el-icon-back" class="back-button" @click="goBack">Back</el-button>
        <h1>Vouchers</h1>
        <div class="header-spacer"></div>
      </div>
    </el-header>

    <el-main>
      <div class="claim-panel">
        <div>
          <h2>Claim a voucher</h2>
          <p>Use the recipient email and voucher ID from your invitation. After claim, the voucher is bound to this account.</p>
        </div>
        <el-button type="primary" icon="el-icon-link" @click="claimVisible = true">Claim</el-button>
      </div>

      <el-table v-loading="loading" :data="items" border size="small" class="voucher-table">
        <el-table-column label="Voucher" min-width="220">
          <template slot-scope="scope">
            <div class="strong">{{ scope.row.voucher_code || scope.row.id }}</div>
            <div class="muted">{{ formatType(scope.row.voucher_type) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="130">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="small">{{ formatStatus(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Email" min-width="220">
          <template slot-scope="scope">
            <div>{{ scope.row.recipient_email || '-' }}</div>
            <div v-if="scope.row.claimed_by_email" class="muted">bound to {{ scope.row.claimed_by_email }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Redeemed" width="150">
          <template slot-scope="scope">{{ formatDate(scope.row.redeemed_at) }}</template>
        </el-table-column>
        <el-table-column label="Actions" width="170" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.voucher_type === 'subscription_14d'"
              size="mini"
              type="primary"
              :disabled="!canRedeem(scope.row)"
              :loading="redeemingId === rowId(scope.row)"
              @click="redeemSubscription(scope.row)"
            >
              Redeem 14d
            </el-button>
            <span v-else class="muted">Use at checkout</span>
          </template>
        </el-table-column>
      </el-table>
    </el-main>

    <el-dialog title="Claim Voucher" :visible.sync="claimVisible" width="520px" :close-on-click-modal="false">
      <el-form ref="claimForm" :model="claimForm" :rules="claimRules" label-width="130px" size="small">
        <el-form-item label="Voucher ID" prop="voucher_code">
          <el-input v-model.trim="claimForm.voucher_code" placeholder="Voucher ID" />
        </el-form-item>
        <el-form-item label="Recipient email" prop="recipient_email">
          <el-input v-model.trim="claimForm.recipient_email" type="email" placeholder="invited@example.com" />
        </el-form-item>
        <el-form-item label="Code" prop="verification_code">
          <el-input v-model.trim="claimForm.verification_code" placeholder="Email code">
            <el-button slot="append" :loading="sendingCode" @click="requestCode">Send code</el-button>
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="claimVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="claiming" @click="submitClaim">Bind to my account</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  claimVoucher,
  listMyVouchers,
  redeemSubscriptionVoucher,
  requestVoucherClaimCode
} from '@/apis/front'

export default {
  name: 'MyVouchers',
  data() {
    return {
      loading: false,
      claiming: false,
      sendingCode: false,
      redeemingId: '',
      claimVisible: false,
      items: [],
      claimForm: {
        voucher_code: '',
        recipient_email: '',
        verification_code: ''
      },
      claimRules: {
        voucher_code: [{ required: true, message: 'Enter the voucher ID.', trigger: 'blur' }],
        recipient_email: [
          { required: true, message: 'Enter the recipient email.', trigger: 'blur' },
          { type: 'email', message: 'Enter a valid email.', trigger: 'blur' }
        ],
        verification_code: [{ required: true, message: 'Enter the email code.', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      if (!this.$store.state.user_id) return
      this.loading = true
      try {
        const res = await listMyVouchers({ user_id: this.$store.state.user_id })
        if (res.data.code === 0) {
          this.items = (res.data.data && res.data.data.items) || []
        } else {
          this.$message.error(res.data.msg || 'Failed to load vouchers')
        }
      } catch (e) {
        this.$message.error('Failed to load vouchers')
      } finally {
        this.loading = false
      }
    },
    requestCode() {
      this.$refs.claimForm.validateField('voucher_code', async (voucherError) => {
        if (voucherError) return
        this.$refs.claimForm.validateField('recipient_email', async (emailError) => {
          if (emailError) return
          this.sendingCode = true
          try {
            const res = await requestVoucherClaimCode({
              voucher_code: this.claimForm.voucher_code,
              recipient_email: this.claimForm.recipient_email
            })
            if (res.data.code === 0) {
              this.$message.success('Verification code sent')
            } else {
              this.$message.error(res.data.msg || 'Failed to send code')
            }
          } catch (e) {
            this.$message.error('Failed to send code')
          } finally {
            this.sendingCode = false
          }
        })
      })
    },
    submitClaim() {
      this.$refs.claimForm.validate(async (valid) => {
        if (!valid) return
        this.claiming = true
        try {
          const res = await claimVoucher({
            ...this.claimForm,
            user_id: this.$store.state.user_id
          })
          if (res.data.code === 0) {
            this.$message.success('Voucher bound to your account')
            this.claimVisible = false
            this.fetchData()
          } else {
            this.$message.error(res.data.msg || 'Failed to claim voucher')
          }
        } catch (e) {
          this.$message.error('Failed to claim voucher')
        } finally {
          this.claiming = false
        }
      })
    },
    async redeemSubscription(row) {
      const id = this.rowId(row)
      if (!id) return
      this.redeemingId = id
      try {
        const res = await redeemSubscriptionVoucher({
          user_id: this.$store.state.user_id,
          voucher_id: id
        })
        if (res.data.code === 0) {
          this.$message.success('Subscription extended by 14 days')
          this.$store.dispatch('fetchPlatformSubscription')
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed to redeem voucher')
        }
      } catch (e) {
        this.$message.error('Failed to redeem voucher')
      } finally {
        this.redeemingId = ''
      }
    },
    canRedeem(row) {
      return row && ['issued', 'claimed'].includes(row.status)
    },
    rowId(row) {
      if (!row) return ''
      if (row.id) return row.id
      const id = row._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || ''
    },
    formatType(type) {
      const map = {
        subscription_14d: '14-day subscription',
        collection_purchase: 'Collection purchase'
      }
      return map[type] || type || '-'
    },
    formatStatus(status) {
      const map = {
        draft: 'Draft',
        issued: 'Issued',
        claimed: 'Claimed',
        redeemed: 'Redeemed',
        voided: 'Voided'
      }
      return map[status] || status || '-'
    },
    statusTag(status) {
      if (status === 'redeemed') return 'success'
      if (status === 'claimed') return 'warning'
      if (status === 'voided') return 'info'
      return ''
    },
    formatDate(ts) {
      const value = Number(ts || 0)
      if (!value) return '-'
      return dayjs.unix(value).format('YYYY-MM-DD HH:mm')
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.my-vouchers {
  width: 100%;
}

.el-header {
  height: 72px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #409eff;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1180px;
  padding: 0 20px;
  box-sizing: border-box;
}

.header-content h1 {
  margin: 0;
  font-size: 24px;
}

.header-spacer {
  width: 72px;
}

.back-button {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
}

.claim-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1100px;
  margin: 16px auto;
  padding: 16px 18px;
  border: 1px solid #ebeef5;
  background: #fff;
  box-sizing: border-box;
}

.claim-panel h2 {
  margin: 0 0 4px;
  font-size: 18px;
  color: #303133;
}

.claim-panel p {
  margin: 0;
  color: #606266;
  font-size: 13px;
}

.voucher-table {
  max-width: 1100px;
  margin: 0 auto;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.muted {
  color: #909399;
  font-size: 12px;
}
</style>
